import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faExpand, 
  faCompress,
  faChevronLeft,
  faChevronRight,
  faSearchPlus,
  faSearchMinus,
  faFilePdf,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import './PDFViewer.css';

// Import PDF.js
import * as pdfjs from 'pdfjs-dist';

// Set the worker source - use local copy for reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PDFViewer = ({ 
  isOpen, 
  onClose, 
  documentUrl, 
  documentTitle = "PDF Document"
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isLoadingRef = useRef(false); // Track loading state to prevent duplicate requests
  const currentDocumentUrl = useRef(null); // Track current document to prevent reloading same PDF
  
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Load PDF when document URL changes
  useEffect(() => {
    if (isOpen && documentUrl && documentUrl !== currentDocumentUrl.current && !isLoadingRef.current) {
      loadPDF();
    }
    
    // Reset when modal closes
    if (!isOpen) {
      isLoadingRef.current = false;
      currentDocumentUrl.current = null;
      if (pdfDoc) {
        pdfDoc.destroy();
        setPdfDoc(null);
      }
      setCurrentPage(1);
      setTotalPages(0);
      setScale(1.2);
      setIsLoading(true);
      setHasError(false);
      setErrorMessage('');
      setIsFullscreen(false);
    }
    
    return () => {
      // Cleanup on unmount
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [isOpen, documentUrl]);

  // Render page when PDF or page changes
  useEffect(() => {
    if (pdfDoc && currentPage <= totalPages) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale]);

  const loadPDF = async () => {
    // Prevent duplicate loading
    if (isLoadingRef.current || documentUrl === currentDocumentUrl.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      currentDocumentUrl.current = documentUrl;
      setIsLoading(true);
      setHasError(false);
      setErrorMessage('');
      
      // Clean up previous PDF
      if (pdfDoc) {
        pdfDoc.destroy();
        setPdfDoc(null);
      }
      
      // Add credentials and headers for authentication
      const loadingTask = pdfjs.getDocument({
        url: documentUrl,
        httpHeaders: {
          'Accept': 'application/pdf',
        },
        withCredentials: true,
        isEvalSupported: false, // Disable eval for security
        disableAutoFetch: true, // Disable auto-fetching for security
        disableStream: false,
        disableRange: false
      });

      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setIsLoading(false);
      setHasError(true);
      setErrorMessage(error.message || 'Failed to load PDF document');
    } finally {
      isLoadingRef.current = false;
    }
  };

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      const viewport = page.getViewport({ scale });
      
      // Set canvas dimensions
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Allow canvas to be its natural size for proper scrolling when zoomed
      canvas.style.width = viewport.width + 'px';
      canvas.style.height = viewport.height + 'px';

      // Render the page
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error('Error rendering page:', error);
      setHasError(true);
      setErrorMessage('Failed to render PDF page');
    }
  };

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Disable right-click context menu to prevent saving
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  if (!isOpen) return null;

  return (
    <div className="pdf-viewer-overlay">
      <div className={`pdf-viewer-modal ${isFullscreen ? 'fullscreen' : ''}`}>
        {/* Header */}
        <div className="pdf-viewer-header">
          <div className="pdf-info">
            <FontAwesomeIcon icon={faFilePdf} className="me-2 text-danger" />
            <h5 className="mb-0 text-white">{documentTitle}</h5>
            {totalPages > 0 && (
              <small className="text-light ms-3">
                Page {currentPage} of {totalPages}
              </small>
            )}
          </div>
          
          <div className="pdf-controls d-flex align-items-center gap-2">
            {/* Navigation Controls */}
            {totalPages > 1 && (
              <>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  title="Previous Page"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                
                <div className="page-input-group d-flex align-items-center mx-2">
                  <input
                    type="number"
                    className="form-control form-control-sm text-center"
                    style={{ width: '60px' }}
                    value={currentPage}
                    min="1"
                    max={totalPages}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (!isNaN(page)) goToPage(page);
                    }}
                  />
                </div>
                
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  title="Next Page"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                
                <div className="vr text-light opacity-25 mx-2"></div>
              </>
            )}
            
            {/* Zoom Controls */}
            <button
              className="btn btn-sm btn-outline-light"
              onClick={zoomOut}
              disabled={scale <= 0.5}
              title="Zoom Out"
            >
              <FontAwesomeIcon icon={faSearchMinus} />
            </button>
            
            <span className="text-light small mx-2">
              {Math.round(scale * 100)}%
            </span>
            
            <button
              className="btn btn-sm btn-outline-light"
              onClick={zoomIn}
              disabled={scale >= 3.0}
              title="Zoom In"
            >
              <FontAwesomeIcon icon={faSearchPlus} />
            </button>
            
            <div className="vr text-light opacity-25 mx-2"></div>
            
            {/* Fullscreen Toggle */}
            <button
              className="btn btn-sm btn-outline-light"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
            
            {/* Close Button */}
            <button
              className="btn btn-sm btn-outline-light"
              onClick={onClose}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="pdf-viewer-content" ref={containerRef}>
          {isLoading && (
            <div className="pdf-viewer-loading">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h6 className="text-white">Loading PDF...</h6>
              <p className="text-light">Please wait while we process the document.</p>
            </div>
          )}

          {hasError && (
            <div className="pdf-viewer-error">
              <div className="text-warning mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
              </div>
              <h6 className="text-white">Failed to Load PDF</h6>
              <p className="text-light mb-3">{errorMessage}</p>
              <button 
                className="btn btn-primary"
                onClick={loadPDF}
              >
                Try Again
              </button>
            </div>
          )}

          {!isLoading && !hasError && pdfDoc && (
            <div 
              className="pdf-canvas-container"
              onContextMenu={handleContextMenu}
            >
              <canvas 
                ref={canvasRef}
                className="pdf-canvas"
              />
            </div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      <div className="pdf-viewer-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default PDFViewer; 